"use client";

import { DehydratedState } from "@tanstack/react-query";
import { SessionProvider } from "next-auth/react";
import { Session } from "next-auth";
import QueryClientProviderWrapper from "@/src/components/QueryClientProviderWrapper";
import ToastProvider from "@/src/components/Toast/ToastProvider";
import { AnalyticsContextProvider } from "@/libs/analytics/AnalyticsProvider";
import OptimizelyProviderWrapper from "@/src/services/optimizely/OptimizelyProviderWrapper";
import { AppGeoLocProvider } from "@/libs/geo/AppGeoLocProvider";
import { AkamaiGeoCookie } from "nvs-constants";

type Props = {
  children: React.ReactNode;
  dehydratedState?: DehydratedState;
  session?: Session | null;
  geoLoc?: AkamaiGeoCookie;
  dataFromOptimizely?: string | object;
};

const RootProviders: React.FC<Props> = ({
  dehydratedState,
  session,
  children,
  dataFromOptimizely,
  geoLoc,
}) => {
  return (
    <AppGeoLocProvider geoLoc={geoLoc}>
      {/* Little background here:
        The default behavior of the SessionProvider is to call getSession on the client 
        if it is not passed to the provider. The default behavior here is the refetch interval is set to 0,
        which means that next auth does not poll for the session, refetchWhenOffline is false - meaning
        it wont check for online/offline, and it will re-fetch session when window is focused.
        We are leaving refetch session when window is focused bc after testing it 
        it seems to not be a performance issue - we can reexamine this in the future.
         note that the code: says in the next major version some of these defaults might change.
      */}
      <SessionProvider
        // we pass the session from the server to the client
        // so that we can use the session in the client
        // and prevent the call of getSession on the client
        session={session}
        // Re-fetches session when window is focused - this is the default
        // https://next-auth.js.org/getting-started/client#refetch-on-window-focus
        refetchOnWindowFocus={true}
      >
        <OptimizelyProviderWrapper
          dataFromOptimizely={dataFromOptimizely}
          upmId={session?.user?.upmId}
        >
          <QueryClientProviderWrapper dehydratedState={dehydratedState}>
            <ToastProvider>
              <AnalyticsContextProvider country={geoLoc?.country}>
                {/* @TODO add back route progress */}
                {children}
              </AnalyticsContextProvider>
            </ToastProvider>
          </QueryClientProviderWrapper>
        </OptimizelyProviderWrapper>
      </SessionProvider>
    </AppGeoLocProvider>
  );
};

export default RootProviders;
